import {
  addYears,
  differenceInCalendarMonths,
  format,
  getTime,
  intervalToDuration
} from 'date-fns';
import numeral from "numeral";
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Shape } from "../core/global";


import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import styles from "../styles/components/purchase-location-modal.module.scss";
import { LatLonBanner } from './mouse-coordinates';

import { Badge, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { AccountContext } from '../context/account';
import { MapDataContext } from '../context/map-data';
import tableStyles from "../styles/components/table.module.scss";
import { ModalTitle } from './modal-title';

import { addCartItem } from '../api/cart';
import { getDataSchema } from '../api/data';
import { UpdateExploreDataProps } from '../context/explore';
import { DataSourceModel, ModelTypes } from '../core/global';
import { validateFields } from '../core/validations';
import { tokensModal } from '../data/content';
import { estimateCost, itemExistsInArray } from '../helpers/cart';
import { applyTimeFrom, calculateCellResolution, isNumber } from '../helpers/core';
import { trackEvent } from '../helpers/tracking';
import useLoggedIn from '../hooks/useLoggedIn';
import { DateRangeModal, type DateRange, type DefinedRange } from "./date-range-modal";
import { ErrorMessage, WarningMessage } from './error-message';
import { CreateForm, FormObject } from './form-components';
import { InfoModal } from './info-modal';
import Loader from './loader';
import { LoginPanel } from './login-panel';
import { ModelSelect } from './models-switch';
import { PaymentSelector } from './payment-selector';

interface SummaryCostByPaymentMethodProps {
  startDate: Date | undefined
  endDate: Date | undefined
  costPerYear: number | null
}

export const SummaryCostByPaymentMethod = ({
  startDate,
  endDate,
  costPerYear,
}: SummaryCostByPaymentMethodProps) => {
  const [showTokenModal, setShowTokenModal] = useState(false)
  
  const {
    paymentType,
    isVatExempt,
    userPaymentMethods,
  } = useContext(AccountContext);

  const hasPayments = useMemo(() => {
    return !(!userPaymentMethods || (Array.isArray(userPaymentMethods) && userPaymentMethods.length === 0))
  }, [userPaymentMethods])

  const userIsLoggedIn = useLoggedIn();
  
  const itemCost = useMemo(() => {
    return estimateCost(startDate, endDate, costPerYear)
  }, [startDate, endDate])

  if (!hasPayments && userIsLoggedIn) {
    return (
      <>
        <div className={styles.costRow}>
          <p><strong>Please <a href="https://www.metoceanworks.com/contact/" target="_blank">contact the MetOceanWorks team</a> for assistance with this purchase.</strong></p>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.costRow}>
        <h4>Cost</h4>
        <table className={tableStyles.table}>
          <tbody>
            {paymentType === "subscription" && 
              <tr>
                <th className={tableStyles.fieldName}>Included in your subscription:</th>
                <td className={tableStyles.currency}>FREE</td>
              </tr>
            }
           {paymentType === "card" && 
              <tr>
                <th className={tableStyles.fieldName}>GBP (Ex. VAT):</th>
                <td className={tableStyles.currency}>{itemCost === 0 ? `Free` : `£${numeral(itemCost).format("0,0.00")}`}</td>
              </tr>
            }
            {paymentType === "tokens" && itemCost !== null &&
              <tr>
                <th className={tableStyles.fieldName}>
                  <Badge
                    className={styles.badge}
                    badgeContent={"?"} 
                    color="info" 
                    onClick={()=> {
                      setShowTokenModal(true)
                    }}
                  >
                  Tokens
                  </Badge>
                </th>
                <td className={tableStyles.currency}>{numeral(Math.ceil(itemCost/Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR))).format("0,0")}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <InfoModal
        open={showTokenModal}
        title={tokensModal.title}
        content={tokensModal.content}
        maxWidth="sm"
        onClose={() => {
          setShowTokenModal(false)
        }}
      />
    </>
  )
}

interface PurchaseLocationModalProps {
  onClose: () => void
}

export const PurchaseLocationModal = ({
  onClose
}: PurchaseLocationModalProps) => {
  const [defaultValues, setDefaultValues] = useState<FormObject | null>(null)
  const [loading, setLoading] = useState(false)
  const [purchaseModel, setPurchaseModel] = useState<DataSourceModel | null>()
  const [itemProjectId, setItemProjectId] = useState<number | null>(null)
  const [vectors, setVectors] = useState<any>(null)
  const [description, setDescription] = useState<any>(null)
  const [timeline, setTimeline] = useState<any>(null)
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);
  const [configError, setConfigError] = useState<string[] | null>([]);
  
  const {
    meshDataGeoJson,
    getFeatureLocation,
    dataSources,
    dataSourceModel,
  } = useContext(MapDataContext);

  const {
    refetchCart,
    setShowPurchaseData,
    cart,
    projects,
  } = useContext(AccountContext);

  useEffect(() => {
    setPurchaseModel(dataSourceModel)
  }, [])

  const {
    lat,
    lon,
  } = useMemo(() => {
    return getFeatureLocation()
  }, [meshDataGeoJson])

  const handleCloseDateModal = () => {
    setDatePickerOpen(false)
  }
  
  const userIsLoggedIn = useLoggedIn();

  const [dateRange, setDateRangeOnSubmit] = useState<DateRange | null | undefined>(null);

  const {startDate, endDate} = useMemo(() => {
    if (dateRange) {
      return {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }
    }
    return {
      startDate: null,
      endDate: null,
    }
  }, [dateRange])

  useEffect(() => {
    const execute = async ({ model, provider, geography, version }: {
      model: string,
      provider: string,
      geography: string
      version: string | null
    }) => {
      setLoading(true)

      const schemaParams: UpdateExploreDataProps = {
        source: "azure",
        model,
        provider,
        geography,
        version,
        lat,
        lon,
        lengthYears: 1,
        preview: true,
        cache: false,
      }

      const result = await getDataSchema({
        ...schemaParams
      });
      const { response, error, message } = result;

      const { description, timeline, vectors } = response || {};
      setVectors(vectors)
      setDescription(description)
      setTimeline(timeline)
      setLoading(false)
    }
    const { model, provider, geography, version = null } = purchaseModel || {}

    if (model && provider && geography && isNumber(lat) && isNumber(lon)) {
      execute({ model, provider, geography, version })
    }

  }, [JSON.stringify(purchaseModel), lat, lon])

  const { cenLat, cenLon, sqm, frequency } = useMemo(() => {
    const sqm = description?.sqm || null
    const model = description?.model || null
    let useSqm = null
    if (sqm !== null && model !== null) {
      useSqm = calculateCellResolution(sqm, model as ModelTypes)
    }
    const frequency = timeline?.frequency || null
    let useFrequency = ''
    if (frequency) {
      const formatted = intervalToDuration({start: 0, end: frequency*1000})
      useFrequency = formatted.hours && formatted.hours > 0 ? `${formatted.hours} hour` : `${formatted.minutes} minutes`
    }
    return {
      frequency: useFrequency,
      sqm: useSqm,
      cenLat: description?.lat ?? lat ?? null,
      cenLon: description?.lon ?? lon ?? null,
    }
  }, [description, timeline, lat, lon])
  
  const {foundInCart, foundInProjects} = useMemo(() => {
    let foundInCart: any[] = []
    let foundInProjects: any[] = []

    const { model, provider, geography, version = null } = purchaseModel || {}

    if (startDate && endDate && model && provider && geography) {
      const item = {
        lat: cenLat,
        lon: cenLon,
        model,
        provider,
        geography,
        version,
        start_date: getTime(new Date(startDate)),
        end_date: getTime(new Date(endDate))
      }
      
      foundInCart = itemExistsInArray({
        data: cart || [],
        ...item
      })
      
      foundInProjects = itemExistsInArray({
        data: projects || [],
        ...item
      })
    }

    return {
      foundInCart,
      foundInProjects
    }
  }, [JSON.stringify(purchaseModel), cenLat, cenLon, startDate, endDate, itemProjectId, cart, projects])

  const years = useMemo(() => {
    if (startDate && endDate) {
      const months = differenceInCalendarMonths(endDate, startDate)
      const years = Math.abs(Math.ceil(months/12))
      return years
    }
    return null
  }, [startDate, endDate])

  const {ranges, minDate, maxDate, costPerYear, config}: {
    ranges: DefinedRange[] | null,
    minDate: Date | null,
    maxDate: Date | null,
    costPerYear: number | null,
    config: Shape[] | null
  } = useMemo(() => {
    let ranges: DefinedRange[] | null = null
    let minDate: Date | null = null
    let maxDate: Date | null = null
    let costPerYear: number | null = null
    let config: Shape[] | null = null

    if (dataSources && purchaseModel) {
      const { key } = purchaseModel
      if (Object.prototype.hasOwnProperty.call(dataSources, key)) {
        config = dataSources[key].options || null
        const modelStartDate = dataSources[key].start_date || null
        const modelEndDate = dataSources[key].end_date || null
        costPerYear = dataSources[key].tokens_cost_per_year || null
        if (modelStartDate && modelEndDate) {
          minDate = new Date(modelStartDate)
          maxDate = new Date(modelEndDate)
          // add one day to maxDate
          // maxDate = addDays(maxDate, 1)
          const months = differenceInCalendarMonths(minDate, maxDate)
          const years = Math.abs(Math.ceil(months/12))
          setDateRangeOnSubmit({
            startDate: minDate,
            endDate: maxDate,
          })
          // Dynamically create ranges based on data availability
          ranges = [{
              label: `Full dataset ${years} years`,
              startDate: minDate,
              endDate: maxDate
            }]
          if (years > 10) {
            ranges.push({
              label: 'Last 10 years',
              startDate: addYears(maxDate, -10),
              endDate: maxDate
            })
          }
          if (years > 5) {
            ranges.push({
              label: 'Last 5 years',
              startDate: addYears(maxDate, -5),
              endDate: maxDate
            })
          }
          if (years > 2) {
            ranges.push({
              label: 'Last 2 years',
              startDate: addYears(maxDate, -2),
              endDate: maxDate
            })
          }
          if (years > 1) {
            ranges.push({
              label: 'Last 1 year',
              startDate: addYears(maxDate, -1),
              endDate: maxDate
            })
          }
        }
      }
    }
    return {
      ranges, 
      minDate, 
      maxDate,
      costPerYear,
      config
    }
  }, [JSON.stringify(dataSources), JSON.stringify(purchaseModel)])

  
  const handleSetDateRangeOnSubmit = useCallback((dateRange: DateRange) => {
    const { startDate, endDate } = dateRange
    if (startDate && endDate) {
      // minDate, maxDate
      // make sure the hours on start date and end date match the hours on model start and end date
      const updatedStartDate = applyTimeFrom(startDate, minDate);
      const updatedEndDate = applyTimeFrom(endDate, maxDate);

      setDateRangeOnSubmit((updatedStartDate && updatedEndDate) ? {
        startDate: updatedStartDate,
        endDate: updatedEndDate,
      } : null);
    }

    handleCloseDateModal(); // close the modal
  }, [minDate, maxDate])

  return (
    <Dialog
      id='purchase-location-modal'
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <ModalTitle id="purchase-location-modal-title" onClose={onClose}>Data point purchase</ModalTitle>
      <DialogContent>
        <div className={styles.summary}>
          {!loading ?
            <>
              {
                (lat === cenLat && lon === cenLon) ?
                  <LatLonBanner lat={lat} lon={lon} enableCopy />
                : 
                  <>
                    <LatLonBanner lat={lat} lon={lon} title={'Selected location'} enableCopy />
                    <LatLonBanner lat={cenLat} lon={cenLon} title={'Data centroid'} enableCopy />
                  </>
                }
                {
                  config && config.length > 0 && 
                    <>
                      <CreateForm
                        className={styles.form}
                        size="medium"
                        allowEdit={false}
                        shape={config}
                        onChange={(vals, key: string) => {
                          setDefaultValues(vals)
                        }}
                        defaultValues={defaultValues}
                      />
                      <ErrorMessage message={configError} />
                    </>
                }
                  {(vectors && vectors.length > 0) ?
                    <p>
                      <strong>Approximate cell resolution:</strong> {numeral(sqm).format("0,0.0")} m<br />
                      <strong>Parameters:</strong> {vectors.map((v: any) => v.name).join(', ')}<br />
                      <strong>Temporal frequency:</strong> {frequency}<br />
                      {costPerYear ? <><strong>Cost per year (ex. VAT):</strong> £{numeral(costPerYear).format("0,0.0")}</> : <><strong>Cost per year:</strong> Free</>}
                    </p>
                    :
                    <WarningMessage message={`No data available for this model at **${lat}, ${lon}**`} />
                  }
              </>
            :
              <div style={{padding: "32px 0"}}>
                <Loader small center />
              </div>
          }
        </div>
        {foundInCart.length > 0 && <WarningMessage message={'This data point is already in the shopping cart.'} />}
        {foundInProjects.length > 0 && <WarningMessage message={'You have previously acquired this data point for given data range and model.'} />}
        <div className={styles.dateRange}>
          <TextField
            id="startAndEnd"
            name="startAndEnd"
            label="Dataset range"
            value={`${startDate ? format(startDate, "dd/MM/yyyy") : '?'} - ${endDate ? format(endDate, "dd/MM/yyyy"): '?'}`}
            autoFocus
            required
            fullWidth
            variant="outlined"
            onClick={() => {
              setDatePickerOpen(true)
            }}
            // color={titleError ? 'error' : 'primary'}
            sx={{ ariaLabel: 'title', marginBottom: '16px' }}
          />
          {years && 
            <Chip onClick={() => {}} label={`${years} years`} variant="outlined" />
          }
        </div>
        <ModelSelect
          dataSources={dataSources}
          id="purchase-model-select"
          value={`${purchaseModel?.key || ''}`}
          label={'Model'}
          onChange={(newModelKey: string) => {
            const [provider, model, geography, version = null] = newModelKey.split('-')
            const newModel = {
              key: newModelKey,
              provider,
              model,
              geography,
              version,
            } as DataSourceModel
              setPurchaseModel(newModel)
            }
          }
        />
        {/* TODO: add project selector once decided what do with this functionality */}
        {/* <ProjectSelector
          showCreateNew
          projectId={null}
          onChange={(id: number | null) => {
            setItemProjectId(id)
          }} 
        /> */}
        <PaymentSelector
          id="purchase-payment-select"
        />
        {startDate && endDate && <SummaryCostByPaymentMethod startDate={startDate} endDate={endDate} costPerYear={costPerYear} />}
        {ranges && minDate && maxDate && startDate && endDate && (
          <DateRangeModal
            open={datePickerOpen}
            onClose={handleCloseDateModal}
            onSubmit={(range: DateRange) => handleSetDateRangeOnSubmit(range)}
            minDate={minDate}
            maxDate={maxDate}
            definedRanges={ranges}
            initialDateRange={{ startDate, endDate }}
          />
        )}
      </DialogContent>
      <>
      <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
          <Button onClick={onClose}>Close</Button>
          {
            userIsLoggedIn ? 
              <Button
                id="add-to-basket-button-purchase-location"
                // loading={loading}
                variant="contained"
                color="success"
                disabled={
                  !startDate || 
                  !endDate || 
                  !purchaseModel ||
                  cenLat === undefined ||
                  cenLon === undefined ||
                  cenLat === null ||
                  cenLon === null
                }
                endIcon={<ShoppingCartIcon sx={{color: '#fff'}} />}
                startIcon={loading && <CircularProgress size={16} color="warning" />}
                onClick={async (e: React.SyntheticEvent) => {
                  e.preventDefault()
                  if (config && config.length > 0) {
                    const {valid, errors} = validateFields(config, defaultValues || {})
                    if (valid === false && errors.length > 0) {
                      const error = errors.map(e => e.message).filter( (i): i is string  => i !== undefined)
                      setConfigError(error)
                      return
                    }
                  }
                  setConfigError(null)

                  const { model, provider, geography, version = null } = purchaseModel || {}

                  if (startDate && endDate && model && provider && geography) {
                    const start_date = applyTimeFrom(startDate, minDate);
                    const end_date = applyTimeFrom(endDate, maxDate);
                    let params: any = {
                      description: {
                        lat,
                        lon,
                      }
                    }
                    if (defaultValues) {
                      params = {
                        ...params,
                        config: defaultValues
                      }
                    }

                    const item = {
                      lat: cenLat,
                      lon: cenLon,
                      model,
                      start_date,
                      end_date,
                      project_id: itemProjectId,
                      provider,
                      geography,
                      version,
                      params,

                    }

                    await addCartItem({...item})
                    refetchCart()
                    setShowPurchaseData(false)
                    trackEvent("add-to-basket", {lat: cenLat, lon: cenLon, model, start_date: format(startDate, "MM/dd/yyyy"), end_date: format(endDate, "MM/dd/yyyy"),})
                  }
                }}
              >
                Add to basket
              </Button>
            :
              <LoginPanel inline />
          }
          
        </DialogActions>
      </>
    </Dialog>

  )
}
